import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Checkbox, Typography, useTheme } from "@mui/material";
import React from "react";
import { Tooltip } from "../../Tooltip";
import type { LineChartLegendConfig } from "../LineChart";

interface LegendItemProps {
  label: string;
  color: string;
  checked?: boolean;
  onToggle?: (checked: boolean) => void;
  tooltip?: string;
}

const LegendItem: React.FC<LegendItemProps> = ({
  label,
  color,
  checked = true,
  onToggle,
  tooltip,
}) => {
  const theme = useTheme();

  return (
    <Box
      alignItems="center"
      display="flex"
      sx={{
        width: "100%",
        gap: 1,
        justifyContent: { xs: "space-between", md: "flex-start" },
      }}
    >
      <Box alignItems="center" display="flex" gap={1}>
        {onToggle && (
          <Checkbox
            checked={checked}
            onChange={(e) => onToggle(e.target.checked)}
            sx={{
              color,
              height: 20,
              width: 20,
              borderRadius: "4px",
              mr: "6px",
              "&.Mui-checked": { color },
            }}
          />
        )}
        <Typography color={theme.colors.text[6]} fontSize={14} fontWeight={500}>
          {label}
        </Typography>
      </Box>

      {tooltip && (
        <Tooltip message={tooltip} placement="right">
          <InfoOutlinedIcon
            sx={{ fontSize: 20, color: theme.colors.secondary[6] }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

interface LineChartLegendProps<T> {
  series: Array<{ key: keyof T; label: string; color: string }>;
  config: LineChartLegendConfig<T>;
}

export const LineChartLegend = <T,>({
  series,
  config,
}: LineChartLegendProps<T>): React.ReactElement => {
  const { visibleSeries, onToggleSeries, tooltips } = config;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        pl: { xs: "8px", md: "60px" },
        pr: "20px",
        justifyContent: "space-between",
      }}
    >
      {series.map((item) => (
        <LegendItem
          checked={visibleSeries[String(item.key)]}
          color={item.color}
          key={String(item.key)}
          label={item.label}
          onToggle={(checked) => onToggleSeries(String(item.key), checked)}
          tooltip={tooltips[item.key]}
        />
      ))}
    </Box>
  );
};
